// frontend/src/admin/blockforms/ArticleForm.js
import React, { useState, useEffect } from 'react';
import RichTextEditor from '../RichTextEditor';
import { buildBlockSave } from './blockSaveTarget';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

const ArticleForm = ({ level, entityId, section, editingBlock, nextOrderIndex, onSaved, onCancel }) => {
    const existing = editingBlock?.content_data || {};

    const [headingEn, setHeadingEn] = useState(existing.en?.heading || '');
    const [headingHi, setHeadingHi] = useState(existing.hi?.heading || '');
    const [summaryEn, setSummaryEn] = useState(existing.en?.short_summary || '');
    const [summaryHi, setSummaryHi] = useState(existing.hi?.short_summary || '');
    const [textEn, setTextEn] = useState(existing.en?.full_text || '');
    const [textHi, setTextHi] = useState(existing.hi?.full_text || '');
    const [featureImage, setFeatureImage] = useState(existing.feature_image || '');
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const hasUnsavedChanges = headingEn || headingHi || summaryEn || summaryHi || textEn || textHi || featureImage;
        if (!hasUnsavedChanges) return;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [headingEn, headingHi, summaryEn, summaryHi, textEn, textHi, featureImage]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await fetch(`${API_BASE}/rich-content/upload-image`, {
                method: 'POST',
                body: formData
            });
            if (!res.ok) throw new Error('Upload failed');
            const data = await res.json();
            setFeatureImage(data.file_path);
        } catch (err) {
            setError('Image upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!headingEn.trim()) {
            setError('English heading is required');
            return;
        }
        setError(null);
        setSaving(true);

        const content_data = {
            feature_image: featureImage || null,
            en: {
                heading: headingEn,
                short_summary: summaryEn,
                full_text: textEn
            }
        };
        if (headingHi || summaryHi || textHi) {
            content_data.hi = {
                heading: headingHi,
                short_summary: summaryHi,
                full_text: textHi
            };
        }

        const { url, method, payload } = buildBlockSave({
            level, entityId, section, editingBlock,
            blockType: 'article',
            contentData: content_data,
            orderIndex: nextOrderIndex,
        });

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Save failed');
            }
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ace-form">
            <h4>{editingBlock ? 'Edit' : 'Add'} Article / Heading</h4>
            {error && <div className="ace-error">{error}</div>}

            <label>Heading (English) *</label>
            <RichTextEditor value={headingEn} onChange={setHeadingEn} placeholder="Heading..." />

            <label>Heading (Hindi, optional)</label>
            <RichTextEditor value={headingHi} onChange={setHeadingHi} placeholder="Hindi heading (optional)..." />
            <label>Feature Image (optional)</label>
            <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
            {featureImage && (
                <div className="ace-image-preview">
                    <img src={featureImage.startsWith('http') ? featureImage : `${API_BASE}/${featureImage}`} alt="" />
                </div>
            )}

            <label>Short Summary (English) — shown in card list</label>
            <textarea rows={2} value={summaryEn} onChange={(e) => setSummaryEn(e.target.value)} />

            <label>Short Summary (Hindi, optional)</label>
            <textarea rows={2} value={summaryHi} onChange={(e) => setSummaryHi(e.target.value)} />

            <label>Full Text (English) — shown when card is opened</label>
            <RichTextEditor value={textEn} onChange={setTextEn} placeholder="Full article text..." />

            <label>Full Text (Hindi, optional)</label>
            <RichTextEditor value={textHi} onChange={setTextHi} placeholder="Hindi text (optional)..." />

            <div className="ace-form-actions">
                <button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button onClick={onCancel} className="ace-cancel-btn">Cancel</button>
            </div>
        </div>
    );
};

export default ArticleForm;